import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  Eye,
  Filter,
  Flame,
  GraduationCap,
  Loader2,
  MapPin,
  MessageCircle,
  Mic,
  Phone,
  PhoneOff,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  UserPlus,
  Users,
  Video,
  X,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ActivityInsightsPanel from "@/components/ActivityInsightsPanel";

const ACTION_META = {
  call_disposition: { icon: Phone, color: "bg-emerald-100 text-emerald-700", label: "Call" },
  attempt_made: { icon: Activity, color: "bg-sky-100 text-sky-700", label: "Attempt" },
  whatsapp_result: { icon: MessageCircle, color: "bg-[#25D366]/20 text-[#128C7E]", label: "WA result" },
  nudge_first: { icon: MessageCircle, color: "bg-amber-100 text-amber-800", label: "WA nudge" },
  nudge_followup: { icon: MessageCircle, color: "bg-amber-100 text-amber-800", label: "WA nudge" },
  voice_note: { icon: Mic, color: "bg-violet-100 text-violet-700", label: "Voice note" },
  lead_registered: { icon: UserPlus, color: "bg-blue-100 text-blue-700", label: "New lead" },
  claim: { icon: Users, color: "bg-indigo-100 text-indigo-700", label: "Claimed" },
  release: { icon: Users, color: "bg-slate-100 text-slate-700", label: "Released" },
  lead_dispositioned_hot: { icon: Flame, color: "bg-rose-100 text-rose-700", label: "→ Hot" },
  status_change: { icon: ChevronDown, color: "bg-slate-100 text-slate-700", label: "Status" },
  enrolled: { icon: GraduationCap, color: "bg-green-100 text-green-700", label: "Enrolled" },
  close: { icon: GraduationCap, color: "bg-green-100 text-green-700", label: "Enrolled" },
  closed_lost: { icon: ThumbsDown, color: "bg-slate-100 text-slate-500", label: "Lost" },
  wrong_number: { icon: PhoneOff, color: "bg-rose-100 text-rose-700", label: "Wrong #" },
};

// Status-specific icons override the default `status_change` icon when meta.to is set.
const STATUS_ICON_BY_TO = {
  "Zoom Done": { icon: Video, color: "bg-violet-100 text-violet-700", label: "Zoom Done" },
  Visited: { icon: MapPin, color: "bg-emerald-100 text-emerald-700", label: "Visited" },
  "Docs Recd": { icon: Activity, color: "bg-teal-100 text-teal-700", label: "Docs Recd" },
  Enrolled: { icon: GraduationCap, color: "bg-green-100 text-green-700", label: "Enrolled" },
  "Closed-lost": { icon: ThumbsDown, color: "bg-slate-100 text-slate-500", label: "Lost" },
  "Wrong number": { icon: PhoneOff, color: "bg-rose-100 text-rose-700", label: "Wrong #" },
};

const FILTERS = [
  { v: "all", l: "All" },
  { v: "call_disposition,attempt_made", l: "Calls" },
  { v: "whatsapp_result,nudge_first,nudge_followup", l: "WhatsApp" },
  { v: "voice_note", l: "Voice notes" },
  { v: "status_change,enrolled,close,closed_lost,wrong_number", l: "Status changes" },
  { v: "lead_registered,lead_dispositioned_hot", l: "Lead events" },
];

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarize(item) {
  const meta = item.meta || {};
  if (item.action === "call_disposition") {
    const outcome = meta.label || meta.outcome || "logged";
    return `Disposition: ${outcome}`;
  }
  if (item.action === "attempt_made") {
    const channel = meta.channel || "—";
    const outcome = meta.outcome ? ` · ${meta.outcome}` : "";
    return `Attempt via ${channel}${outcome}`;
  }
  if (item.action === "whatsapp_result") {
    return `Sent scholarship result via WhatsApp`;
  }
  if (item.action?.startsWith("nudge_")) {
    return `WhatsApp nudge: ${meta.nudge_type || ""}`;
  }
  if (item.action === "voice_note") {
    const dur = meta.duration_ms ? `${Math.round(meta.duration_ms / 1000)}s` : "";
    const preview = meta.preview ? ` · "${meta.preview.slice(0, 60)}${meta.preview.length > 60 ? "…" : ""}"` : "";
    return `Voice note${dur ? ` · ${dur}` : ""}${preview}`;
  }
  if (item.action === "claim") return "Claimed lead";
  if (item.action === "release") return "Released lead";
  if (item.action === "lead_dispositioned_hot") return "Lead became HOT";
  if (
    item.action === "status_change" ||
    item.action === "enrolled" ||
    item.action === "close" ||
    item.action === "closed_lost" ||
    item.action === "wrong_number"
  ) {
    const from = meta.from || "—";
    const to = meta.to || "—";
    return `Status: ${from} → ${to}`;
  }
  return item.action;
}

export default function ActivityFeedPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("all");
  const [counsellorFilter, setCounsellorFilter] = useState("all");
  const [counsellors, setCounsellors] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [dateRange, setDateRange] = useState("today"); // today | 7d | 30d | all
  const [, setTick] = useState(0); // forces re-render so "Last activity: Xm ago" stays fresh

  const isAdmin = user?.role === "admin";

  // Tick every 30s so the freshness label re-computes even when no new items arrive
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const lastActivityLabel = useMemo(() => {
    const at = items[0]?.at || items[0]?.created_at;
    if (!at) return null;
    return relativeTime(at);
  }, [items]);

  const sinceIso = useMemo(() => {
    const now = new Date();
    if (dateRange === "today") {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    if (dateRange === "7d") return new Date(now.getTime() - 7 * 86400000).toISOString();
    if (dateRange === "30d") return new Date(now.getTime() - 30 * 86400000).toISOString();
    return null;
  }, [dateRange]);

  // Scroll to hash anchor (e.g., #leaderboard) once the insights panel mounts.
  // Retries briefly because the panel renders after its async insights fetch.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(t);
      } else if (++tries > 20) {
        clearInterval(t);
      }
    }, 150);
    return () => clearInterval(t);
  }, [location.hash]);

  const fetchPage = async ({ reset = false } = {}) => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (actionFilter !== "all") params.action_type = actionFilter;
      if (counsellorFilter !== "all") params.counsellor_id = counsellorFilter;
      if (sinceIso) params.since = sinceIso;
      if (!reset && cursor) params.cursor = cursor;
      const r = await api.get("/admin/activity-feed", { params });
      const next = r.data.items || [];
      if (reset) {
        setItems(next);
        setNewCount(0);
      } else {
        setItems((prev) => [...prev, ...next]);
      }
      setCursor(r.data.next_cursor);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadCounsellors = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/admin/counsellors").catch(() => ({ data: { counsellors: [] } }));
      // Filter out admins, keep only counsellors for the team filter
      const members = (r.data?.counsellors || []).filter(
        (u) => u.role === "counsellor",
      );
      setCounsellors(members);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    fetchPage({ reset: true });
    loadCounsellors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, counsellorFilter, dateRange]);

  // Poll for new items every 30s
  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(async () => {
      try {
        const newest = items[0]?.at || items[0]?.created_at;
        if (!newest) return;
        const params = { limit: 20, since: newest };
        if (actionFilter !== "all") params.action_type = actionFilter;
        if (counsellorFilter !== "all") params.counsellor_id = counsellorFilter;
        const r = await api.get("/admin/activity-feed", { params });
        const fresh = (r.data.items || []).filter(
          (i) => (i.at || i.created_at) > newest,
        );
        if (fresh.length) setNewCount(fresh.length);
      } catch {
        /* silent */
      }
    }, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, actionFilter, counsellorFilter]);

  const grouped = useMemo(() => {
    const buckets = {};
    items.forEach((it) => {
      const at = it.at || it.created_at;
      const d = new Date(at);
      const today = new Date();
      const yest = new Date(today.getTime() - 86400_000);
      let key = d.toLocaleDateString();
      if (d.toDateString() === today.toDateString()) key = "Today";
      else if (d.toDateString() === yest.toDateString()) key = "Yesterday";
      (buckets[key] = buckets[key] || []).push(it);
    });
    return Object.entries(buckets);
  }, [items]);

  return (
    <div className="min-h-screen bg-[#F6F8FB]" data-testid="activity-feed-page">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-5 pl-16 py-3 flex items-center gap-3 md:pl-5">
          <Eye className="h-5 w-5 text-[#0A2540]" />
          <div className="mr-auto">
            <div className="flex items-center gap-2">
              <span className="font-display text-base font-semibold text-[#0A2540]">
                Meda Eye
              </span>
              <span
                data-testid="meda-live-badge"
                className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 ring-1 ring-red-200"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                Live
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <span>Live · tenant-wide</span>
              {lastActivityLabel && (
                <>
                  <span className="text-slate-300">·</span>
                  <span
                    className="normal-case tracking-normal text-slate-500"
                    data-testid="activity-last-stat"
                    title="Time since the most recent logged action"
                  >
                    Last activity: {lastActivityLabel}
                  </span>
                </>
              )}
            </div>
          </div>
          {newCount > 0 && (
            <button
              type="button"
              onClick={() => fetchPage({ reset: true })}
              data-testid="activity-load-new"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A2540] px-3 py-1 text-xs text-white"
            >
              <RefreshCw className="h-3 w-3" />
              {newCount} new
            </button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchPage({ reset: true })}
            disabled={loading}
            data-testid="activity-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
        <div className="mx-auto max-w-5xl border-t border-slate-100 bg-slate-50/60 px-5 py-2 flex flex-wrap items-center gap-2 md:pl-5 pl-16">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.v}
                onClick={() => setActionFilter(f.v)}
                data-testid={`activity-filter-${f.v.split(",")[0]}`}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  actionFilter === f.v
                    ? "bg-[#0A2540] text-white"
                    : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
          {isAdmin && counsellors.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5">
              <Select value={counsellorFilter} onValueChange={setCounsellorFilter}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="activity-counsellor-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  {counsellors.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(actionFilter !== "all" ||
                counsellorFilter !== "all" ||
                dateRange !== "today") && (
                <button
                  type="button"
                  onClick={() => {
                    setActionFilter("all");
                    setCounsellorFilter("all");
                    setDateRange("today");
                  }}
                  data-testid="activity-clear-filters"
                  title="Reset to default filters"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-[#C9A961] hover:text-[#0A2540]"
                >
                  <X className="h-3 w-3" />
                  Reset
                </button>
              )}
            </div>
          )}
        </div>
        {/* Date range pills */}
        <div
          className="mx-auto max-w-5xl border-t border-slate-100 bg-white px-5 py-2 flex flex-wrap items-center gap-1.5 md:pl-5 pl-16"
          data-testid="activity-date-range"
        >
          <span className="mr-1 text-[10px] uppercase tracking-[0.18em] text-slate-400">
            Showing
          </span>
          {[
            { k: "today", l: "Today" },
            { k: "7d", l: "Last 7 days" },
            { k: "30d", l: "Last 30 days" },
            { k: "all", l: "All time" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setDateRange(opt.k)}
              data-testid={`activity-range-${opt.k}`}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
                dateRange === opt.k
                  ? "border-[#0A2540] bg-[#0A2540] text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-[#0A2540]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-6">
        {isAdmin && (
          <div className="mb-6">
            <ActivityInsightsPanel
              sinceIso={sinceIso}
              refreshKey={items.length}
              onCounsellorClick={(id) =>
                setCounsellorFilter((prev) => (prev === id ? "all" : id))
              }
            />
          </div>
        )}
        {items.length === 0 && !loading && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            <Sparkles className="mx-auto mb-2 h-6 w-6 text-[#C9A961]" />
            No activity yet for the selected filter.
          </div>
        )}

        {grouped.map(([day, list]) => (
          <div key={day} className="mb-8">
            <div className="sticky top-[110px] z-10 mb-2 inline-block rounded-md bg-white/95 px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 backdrop-blur">
              {day}
            </div>
            <ol className="space-y-2">
              {list.map((it) => {
                const toStatus = (it.meta || {}).to;
                let meta = ACTION_META[it.action] || {
                  icon: Sparkles,
                  color: "bg-slate-100 text-slate-700",
                  label: it.action,
                };
                // Status-specific overrides take precedence so the feed reads at a glance
                if (
                  (it.action === "status_change" ||
                    it.action === "enrolled" ||
                    it.action === "close" ||
                    it.action === "closed_lost" ||
                    it.action === "wrong_number") &&
                  toStatus &&
                  STATUS_ICON_BY_TO[toStatus]
                ) {
                  meta = STATUS_ICON_BY_TO[toStatus];
                }
                const Icon = meta.icon;
                return (
                  <li
                    key={it.id || `${it.actor_id}-${it.at}`}
                    data-testid={`activity-item-${it.id || ""}`}
                    onClick={() =>
                      it.lead_id && nav(`/admin?lead_id=${it.lead_id}`)
                    }
                    className="group flex cursor-pointer gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-all hover:border-[#C9A961]/40 hover:shadow-sm"
                  >
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${meta.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-mono-clock">{relativeTime(it.at || it.created_at)}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {meta.label}
                        </Badge>
                        {it.lead_id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              nav(`/admin?lead_id=${it.lead_id}`);
                            }}
                            className="ml-auto inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 transition-colors hover:border-[#0A2540] hover:text-[#0A2540]"
                            data-testid={`activity-open-lead-${it.lead_id}`}
                            title="Open lead drawer"
                          >
                            Open
                            <ChevronDown className="h-3 w-3 -rotate-90" />
                          </button>
                        )}
                      </div>
                      <div className="mt-0.5 text-sm">
                        <span className="font-semibold text-[#0A2540]">
                          {it.actor_name || "System"}
                        </span>
                        {it.lead_name && (
                          <>
                            <span className="text-slate-400"> → </span>
                            <span className="text-[#0A2540]">{it.lead_name}</span>
                          </>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-600">
                        {summarize(it)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}

        {cursor && (
          <div className="text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPage()}
              disabled={loading}
              data-testid="activity-load-more"
            >
              {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Load older activity
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
